import {ActionSource, ActionStatus, MIP_SOURCE} from '@domain/pom'
import {localTimeToTimeZoneTime, timeZoneTimeToLocalTime} from '@hconnect/common/dates/timeZone'
import {Box, Typography, useTheme} from '@mui/material'
import TextField from '@mui/material/TextField'
import {UserSearchField} from '@widgets/common-approvers'
import SimpleMDE from 'easymde'
import {isEqual} from 'lodash'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import SimpleMdeReact from 'react-simplemde-editor'

import {MAX_DESCRIPTION_CHARACTER_LIMIT} from '../../common/validator/actionValidator'
import {ItemValidationResult, ValidationError} from '../../common/validator/validator.types'
import {FEATURE_NAMESPACE} from '../../consts'
import {useTimeZone} from '../../hooks'
import {ActionItem, CreateActionItem} from '../../types'
import {dueDate, removeHTMLTags} from '../../utils'
import {CostLeadershipField} from '../actionDialog/CostLeadershipField'
import {DueDateField} from '../actionDialog/DueDateField'
import {KpiNameField} from '../actionDialog/KpiNameField'
import {MipElementField} from '../actionDialog/MipElementField'
import {NotifyAtField} from '../actionDialog/NotifyAtField'
import {SourceField} from '../actionDialog/SourceField'
import {StatusField} from '../actionDialog/StatusField'
import {ResponsiblePersonField} from '../responsiblePerson/ResponsiblePersonField'

import {ActionConnectedToSection} from './ActionConnectedToSection'

import 'easymde/dist/easymde.min.css'

export type ActionFormFieldKey = keyof ActionItem & keyof CreateActionItem

type ActionFormProps<T> = {
  formValues: Partial<T>
  validationErrors: ItemValidationResult<Partial<ActionItem>>
  handleInputChange: <K extends ActionFormFieldKey>(key: K, value: Partial<ActionItem>[K]) => void
  actionItem: Partial<T>
  enableSpellChecking?: boolean
  showApprovers?: boolean
  isEditMode?: boolean
}

const GROUP_COST_BENCHMARKING: ActionSource = 'groupCostBenchmarking'

const isCostLeadership = ({source, costLeadership}: Partial<ActionItem | CreateActionItem>) =>
  source === GROUP_COST_BENCHMARKING || Boolean(costLeadership)

// locked while the source implies it, and while editing an existing action
const isCostLeadershipDisabled = (
  {source}: Partial<ActionItem | CreateActionItem>,
  isEditMode?: boolean
) => source === GROUP_COST_BENCHMARKING || Boolean(isEditMode)

type HandleInputChange = <K extends ActionFormFieldKey>(
  key: K,
  value: Partial<ActionItem>[K]
) => void

// keeps the source-dependent fields consistent whenever the source changes
const applySourceChange = (
  value: ActionSource | undefined,
  handleInputChange: HandleInputChange
) => {
  handleInputChange('source', value)
  if (value === GROUP_COST_BENCHMARKING) {
    handleInputChange('costLeadership', true)
  }
  if (value !== MIP_SOURCE) {
    handleInputChange('element', undefined)
  }
}

// the Element dropdown only applies to actions sourced from MIP
const MipElementFormSection = <T extends ActionItem | CreateActionItem>({
  formValues,
  validationErrors,
  handleInputChange
}: {
  formValues: Partial<T>
  validationErrors: ItemValidationResult<Partial<ActionItem>>
  handleInputChange: HandleInputChange
}) => {
  if (formValues.source !== MIP_SOURCE) {
    return null
  }

  return (
    <Box my={2}>
      <MipElementField
        value={formValues.element}
        onChange={(value) => handleInputChange('element', value)}
        error={validationErrors.element}
      />
    </Box>
  )
}

export const ActionForm = <T extends ActionItem | CreateActionItem>({
  formValues,
  validationErrors,
  handleInputChange,
  actionItem,
  showApprovers = false,
  isEditMode
}: ActionFormProps<T>) => {
  const theme = useTheme()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timeZone = useTimeZone()

  const options = useMemo<SimpleMDE.Options>(() => {
    return {
      toolbar: ['bold', 'italic', 'strikethrough', '|', 'unordered-list', 'ordered-list'],
      status: false,
      minHeight: '5em',
      inputStyle: 'contenteditable',
      placeholder: `${t('actionDialog.description')} *`
    }
  }, [t])

  const users = formValues.approvers?.map(({user}) => user)

  return (
    <>
      <Box my={2}>
        <TextField
          id="title"
          label={t('actionDialog.title')}
          variant="filled"
          fullWidth={true}
          value={formValues.title}
          required
          onChange={(e) => handleInputChange('title', e.target.value)}
          error={Boolean(validationErrors.title)}
          data-test-id="action-title"
        />
      </Box>

      {!actionItem.questionId && (
        <Box my={2}>
          <SourceField
            value={formValues.source}
            error={Boolean(validationErrors.source)}
            onChange={(value) => applySourceChange(value, handleInputChange)}
          />
        </Box>
      )}

      <MipElementFormSection
        formValues={formValues}
        validationErrors={validationErrors}
        handleInputChange={handleInputChange}
      />

      <Box my={2}>
        <CostLeadershipField
          checked={isCostLeadership(formValues)}
          disabled={isCostLeadershipDisabled(formValues, isEditMode)}
          onChange={(value) => handleInputChange('costLeadership', value)}
        />
      </Box>

      {isCostLeadership(formValues) && (
        <>
          <Box my={2}>
            <KpiNameField
              value={formValues.kpiName}
              onChange={(value) => handleInputChange('kpiName', value)}
              error={validationErrors.kpiName}
              disabled={isEditMode}
            />
          </Box>

          <Box my={2} display="flex" gap={2}>
            <TextField
              label={t('actionDialog.targetCosts')}
              variant="filled"
              fullWidth
              value={formValues.targetCosts ?? ''}
              onChange={(e) => handleInputChange('targetCosts', e.target.value)}
              disabled={isEditMode}
              data-test-id="action-target-costs"
            />
            <TextField
              label={t('actionDialog.actualCostsYtd')}
              variant="filled"
              fullWidth
              value={formValues.actualCostsYtd ?? ''}
              onChange={(e) => handleInputChange('actualCostsYtd', e.target.value)}
              data-test-id="action-actual-costs-ytd"
            />
          </Box>

          <Box my={2} display="flex" gap={2}>
            <TextField
              label={t('actionDialog.targetKpi')}
              variant="filled"
              fullWidth
              value={formValues.targetKpi ?? ''}
              onChange={(e) => handleInputChange('targetKpi', e.target.value)}
              disabled={isEditMode}
              data-test-id="action-target-kpi"
            />
            <TextField
              label={t('actionDialog.actualKpiYtd')}
              variant="filled"
              fullWidth
              value={formValues.actualKpiYtd ?? ''}
              onChange={(e) => handleInputChange('actualKpiYtd', e.target.value)}
              data-test-id="action-actual-kpi-ytd"
            />
          </Box>
        </>
      )}

      <Box my={2} data-test-id="action-description">
        <Box
          component={SimpleMdeReact}
          id="description"
          value={formValues.description || ''}
          onChange={(next) => handleInputChange('description', next)}
          options={options}
          width="100%"
          sx={{
            backgroundColor: theme.palette.grey['100'],
            '.CodeMirror': {
              maxHeight: '320px',
              backgroundColor: theme.palette.grey['100']
            },
            '.CodeMirror-scroll': {
              maxHeight: '300px'
            },
            '.CodeMirror .cm-spell-error:not(.cm-url):not(.cm-comment):not(.cm-tag):not(.cm-word)':
              {
                background: 'unset'
              }
          }}
        />
        <Box display="flex" justifyContent="space-between">
          <Box>
            {validationErrors.description === ValidationError.maxSizeExceeded && (
              <Typography variant="caption" sx={{color: theme.palette.error.light}}>
                {t(validationErrors.description, {maxLength: MAX_DESCRIPTION_CHARACTER_LIMIT})}
              </Typography>
            )}
          </Box>
          <Typography pt={0.5} pr={0.5} variant="caption">
            {removeHTMLTags(formValues.description).length}/{MAX_DESCRIPTION_CHARACTER_LIMIT}
          </Typography>
        </Box>
      </Box>

      {showApprovers && (
        <Box my={2}>
          <UserSearchField
            label={t('actionDialog.approvers')}
            onChange={(newAssignee) => {
              if (newAssignee && !isEqual(newAssignee, users)) {
                handleInputChange(
                  'approvers',
                  newAssignee.map((newAssignee) => {
                    const existingApprover = formValues.approvers?.find(
                      ({user}) => user.id === newAssignee.id
                    )
                    return existingApprover
                      ? existingApprover
                      : {
                          user: newAssignee,
                          status: 'pending'
                        }
                  })
                )
                if (newAssignee.length > 0) {
                  handleInputChange('status', ActionStatus.WaitingForApproval)
                } else {
                  handleInputChange('status', ActionStatus.Pending)
                }
              } else if (!newAssignee) {
                handleInputChange('approvers', [])
                handleInputChange('status', ActionStatus.Pending)
              }
            }}
            value={users || []}
            data-test-id="approver-search"
            sx={{flex: 1}}
          />
        </Box>
      )}

      {showApprovers && !!formValues.approvers?.length && (
        <NotifyAtField
          checked={!!formValues.notifyAt}
          date={formValues.notifyAt}
          onToggle={(enabled) => {
            handleInputChange('notifyAt', enabled ? new Date() : undefined)
          }}
          onDateChange={(date) => handleInputChange('notifyAt', date)}
        />
      )}

      <Box my={2}>
        <ResponsiblePersonField
          error={Boolean(validationErrors.assignee)}
          value={formValues.assignee}
          onChange={(value) => handleInputChange('assignee', value)}
        />
      </Box>

      <Box my={2}>
        <DueDateField
          date={
            formValues.dueDate ? timeZoneTimeToLocalTime(formValues.dueDate, timeZone) : undefined
          }
          onChange={(value) => {
            // interpret the Date as local date of the plant's time zone
            const date = value
              ? dueDate(localTimeToTimeZoneTime(value, timeZone), timeZone)
              : undefined
            handleInputChange('dueDate', date)
          }}
          error={!!validationErrors.dueDate}
        />
      </Box>

      {actionItem.questionId && <ActionConnectedToSection value={actionItem.questionId} />}

      <Box my={2}>
        <StatusField
          value={formValues.status}
          error={Boolean(validationErrors.status)}
          onChange={(value) => handleInputChange('status', value)}
          needsApproval={!!formValues.approvers?.length}
        />
      </Box>
    </>
  )
}
